import React, { useState } from 'react';

interface JsonViewerProps {
  data: unknown;
  className?: string;
  maxDepth?: number;
}

interface JsonNodeProps {
  data: unknown;
  depth: number;
  maxDepth: number;
  keyName?: string;
  isLast?: boolean;
}

function JsonNode({ data, depth, maxDepth, keyName, isLast = false }: JsonNodeProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(depth >= maxDepth);
  
  const indent = depth * 16;
  const isExpandable = typeof data === 'object' && data !== null && Object.keys(data).length > 0;
  
  const getValueType = (value: unknown): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };
  
  const getValueColor = (type: string): string => {
    switch (type) {
      case 'string': return 'text-green-600';
      case 'number': return 'text-primary-600';
      case 'boolean': return 'text-primary-600';
      case 'null': return 'text-gray-500';
      case 'array': return 'text-orange-600';
      case 'object': return 'text-red-600';
      default: return 'text-gray-900';
    }
  };
  
  const formatValue = (value: unknown): string => {
    const type = getValueType(value);
    switch (type) {
      case 'string': return `"${value}"`;
      case 'null': return 'null';
      case 'array': return `Array(${Array.isArray(value) ? value.length : 0})`;
      case 'object': return `Object(${value && typeof value === 'object' ? Object.keys(value).length : 0})`;
      default: return String(value);
    }
  };
  
  const renderPrimitive = (value: unknown): JSX.Element => {
    const type = getValueType(value);
    const color = getValueColor(type);
    
    return (
      <span className={`font-mono text-sm ${color}`}>
        {formatValue(value)}
      </span>
    );
  };
  
  const renderKey = (key: string): JSX.Element => (
    <span className="font-mono text-sm text-gray-700 font-medium">
      "{key}":
    </span>
  );
  
  const toggleCollapse = (): void => {
    if (isExpandable) {
      setIsCollapsed(!isCollapsed);
    }
  };
  
  if (typeof data !== 'object' || data === null) {
    return (
      <div style={{ paddingLeft: `${indent}px` }} className="py-0.5">
        {keyName && (
          <>
            {renderKey(keyName)}
            <span className="mx-1">:</span>
          </>
        )}
        {renderPrimitive(data)}
        {!isLast && <span className="text-gray-500">,</span>}
      </div>
    );
  }
  
  const entries: [string, unknown][] = Array.isArray(data) 
    ? data.map((item, index) => [index.toString(), item])
    : typeof data === 'object'
      ? Object.entries(data as Record<string, unknown>)
      : [];
  
  return (
    <div>
      <div 
        style={{ paddingLeft: `${indent}px` }} 
        className={`py-0.5 ${isExpandable ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={toggleCollapse}
      >
        {keyName && (
          <>
            {renderKey(keyName)}
            <span className="mx-1">:</span>
          </>
        )}
        
        {isExpandable && (
          <span className="inline-flex items-center mr-2">
            <span className="text-gray-400 text-xs">
              {isCollapsed ? '▶' : '▼'}
            </span>
          </span>
        )}
        
        <span className={`font-mono text-sm ${getValueColor(getValueType(data))}`}>
          {Array.isArray(data) ? '[' : '{'}
        </span>
        
        {isCollapsed && isExpandable && (
          <span className="text-gray-400 text-sm ml-1">
            ...{entries.length} item{entries.length !== 1 ? 's' : ''}
          </span>
        )}
        
        {isCollapsed && (
          <span className={`font-mono text-sm ${getValueColor(getValueType(data))}`}>
            {Array.isArray(data) ? ']' : '}'}
          </span>
        )}
        
        {!isCollapsed && !isExpandable && (
          <span className={`font-mono text-sm ${getValueColor(getValueType(data))}`}>
            {Array.isArray(data) ? ']' : '}'}
          </span>
        )}
        
        {!isLast && isCollapsed && <span className="text-gray-500">,</span>}
      </div>
      
      {!isCollapsed && isExpandable && (
        <>
          {entries.map(([key, value], index) => (
            <JsonNode
              key={key}
              data={value}
              depth={depth + 1}
              maxDepth={maxDepth}
              keyName={Array.isArray(data) ? undefined : key}
              isLast={index === entries.length - 1}
            />
          ))}
          <div style={{ paddingLeft: `${indent}px` }} className="py-0.5">
            <span className={`font-mono text-sm ${getValueColor(getValueType(data))}`}>
              {Array.isArray(data) ? ']' : '}'}
            </span>
            {!isLast && <span className="text-gray-500">,</span>}
          </div>
        </>
      )}
    </div>
  );
}

export function JsonViewer({ data, className = '', maxDepth = 2 }: JsonViewerProps): JSX.Element {
  if (data === undefined) {
    return (
      <div className={`font-mono text-sm text-gray-500 ${className}`}>
        undefined
      </div>
    );
  }
  
  return (
    <div className={`bg-gray-50 rounded-lg p-3 border overflow-auto ${className}`}>
      <JsonNode data={data} depth={0} maxDepth={maxDepth} />
    </div>
  );
}